import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateCustomer, useCreateSubscription } from "@/api/stripe";
import Check from "@/assets/icons/check";
import { Button } from "@/components/ui/button";
import DiseaseLabel from "@/components/ui/disease-label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface CheckoutCardProps {
  title: string;
  monthlyPriceId: string;
  monthlyPrice: number;
  yearlyPriceId: string;
  yearlyPrice: number;
  features: Array<string>;
  highlighted?: boolean;
}

const CheckoutCard: FC<CheckoutCardProps> = ({
  title,
  monthlyPriceId,
  monthlyPrice,
  yearlyPriceId,
  yearlyPrice,
  features,
  highlighted = false,
}) => {
  const navigate = useNavigate();
  const { mutateAsync: createCustomer } = useCreateCustomer();
  const { mutateAsync: createSubscription } = useCreateSubscription();

  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (priceId: string) => {
    setIsLoading(true);
    await createCustomer();
    const subscription = await createSubscription(priceId);
    setIsLoading(false);
    navigate("/checkout", {
      state: { clientSecret: subscription.clientSecret, priceId },
    });
  };

  return (
    <div
      className={cn(
        "rounded-lg w-[40%]",
        highlighted
          ? "p-[4px] bg-gradient-to-r from-[#FC9CAC] via-[#B5307D] to-[#2C00B7]"
          : "border border-[#E2E8F0]"
      )}
    >
      <div className="bg-white flex flex-col rounded-lg p-8 gap-8 w-full h-full">
        <div className="flex flex-col gap-1">
          <DiseaseLabel
            customLabel="Abonnement"
            className="bg-brand-50 border-subscription-200 text-brand-700 w-fit font-medium text-sm"
          />
          <div>{title}</div>
        </div>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="flex">
              <div className="text-7xl font-bold">{`${monthlyPrice}€`}</div>
              <div className="text-2xl font-bold self-end">{"/mois"}</div>
            </div>
            <div
              onClick={async () => {
                await handleClick(yearlyPriceId);
              }}
            >
              {`ou ${yearlyPrice}€ annuel`}
            </div>
          </div>
          <Button
            size={"lg"}
            onClick={async () => {
              await handleClick(monthlyPriceId);
            }}
            loading={isLoading}
          >
            {"Commencer"}
          </Button>
        </div>
        <Separator />
        <div className="flex flex-col py-2 gap-4">
          {features.map((feature) => (
            <div key={feature} className="flex gap-4">
              <Check />
              <div>{feature}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CheckoutCard;
